import { createContext, ReactNode, useEffect } from 'react'
import useLocalStorage from '../hooks/useLocalStorage'
import { useAuth } from 'react-oidc-context'

export enum ThemeVariation {
  Light,
  Dark,
  System,
}

interface InitContextProps {
  theme: ThemeVariation
  isDarkTheme: boolean
  changeTheme: (theme: ThemeVariation) => void
  userRole: string
  userName: string
}

export const ProfileContext = createContext({} as InitContextProps)

interface ProfileProviderProps {
  children: ReactNode
}

export const ProfileProvider = ({ children }: ProfileProviderProps) => {
  const isSystemDarkTheme = () =>
    window.matchMedia('(prefers-color-scheme: dark)').matches

  const [theme, setTheme] = useLocalStorage('theme', ThemeVariation.System)
  const [isDarkTheme, setDarkTheme] = useLocalStorage<boolean>(
    'isDarkTheme',
    isSystemDarkTheme(),
  )

  useEffect(() => {
    if (theme === ThemeVariation.Dark) {
      setDarkTheme(true)
    } else if (theme === ThemeVariation.Light) {
      setDarkTheme(false)
    } else {
      setDarkTheme(isSystemDarkTheme)
    }
  }, [theme, setDarkTheme])

  const changeTheme = (theme: ThemeVariation) => {
    setTheme(theme)
  }

  const auth = useAuth()
  const userRole = auth?.user?.profile['user_role'] as string
  const userName = auth?.user?.profile.sub ?? ''

  const value = {
    theme,
    isDarkTheme,
    changeTheme,
    userRole,
    userName,
  }

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  )
}
